import React from 'react';
import { Extension } from '../types';
import { User } from '@supabase/supabase-js';

interface SidebarProps {
  user: User | null;
  onLogin: () => void;
  onLogout: () => void;
  activeSection: string;
  onSectionChange: (section: string) => void;
  activatedExtensions: Extension[];
  onExtensionClick: (extensionId: string) => void;
  activeExtensionSettings: string | null;
  recentlyAddedExtension: string | null;
}

const Sidebar: React.FC<SidebarProps> = ({
  user,
  onLogin,
  onLogout,
  activeSection,
  onSectionChange,
  activatedExtensions,
  onExtensionClick,
  activeExtensionSettings,
  recentlyAddedExtension
}) => {
  const [extensionsExpanded, setExtensionsExpanded] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);

  // Auto-collapse sidebar on mobile/tablet
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      }
    };

    // Check on mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-expand extensions menu when a new extension is added
  React.useEffect(() => {
    if (recentlyAddedExtension && !extensionsExpanded) {
      setExtensionsExpanded(true);
    }
  }, [recentlyAddedExtension, extensionsExpanded]);

  // Collapse extensions submenu when sidebar is collapsed
  React.useEffect(() => {
    if (sidebarCollapsed) {
      setExtensionsExpanded(false);
    }
  }, [sidebarCollapsed]);

  const handleExtensionsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (activatedExtensions.length > 0) {
      setExtensionsExpanded(!extensionsExpanded);
    }
    onSectionChange('extensions');
  };

  return (
    <aside className={`bg-black p-4 flex flex-col border-r-2 border-white shrink-0 transition-all duration-300 ${
      sidebarCollapsed ? 'w-20' : 'w-64'
    }`}>
      {/* Logo Area */}
      <div className={`mb-6 flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
        {!sidebarCollapsed ? (
          <>
            <img
              src="/dork-logo.png"
              alt="Dork Logo"
              className="w-12 h-12 object-contain shrink-0"
            />
            <div className="flex flex-col">
              <h1 className="text-2xl font-bold text-white leading-tight">dork</h1>
              <p className="text-xs text-gray-400">i am bot</p>
            </div>
          </>
        ) : (
          <img
            src="/dork-logo.png"
            alt="Dork Logo"
            className="w-12 h-12 object-contain"
          />
        )}
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className="mb-4 p-2 text-gray-400 hover:text-white hover:bg-white hover:bg-opacity-10 transition-colors border-2 border-gray-600 hover:border-white flex items-center justify-center"
        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <i className={`fa-solid ${sidebarCollapsed ? 'fa-bars' : 'fa-angles-left'}`}></i>
      </button>
      
      <nav className="flex-grow space-y-2" id="sidebar-nav">
        <div>
          <button
            onClick={handleExtensionsClick}
            className={`sidebar-item flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between space-x-3'} p-2 cursor-pointer w-full text-left ${
              activeSection === 'extensions' || activeSection === 'extension-settings' ? 'active' : ''
            }`}
            title={sidebarCollapsed ? 'extensions' : ''}
          >
            <div className={`flex items-center ${sidebarCollapsed ? '' : 'space-x-3'}`}>
              <i className="fa-solid fa-puzzle-piece w-5 text-center"></i>
              {!sidebarCollapsed && <span>extensions</span>}
            </div>
            {!sidebarCollapsed && activatedExtensions.length > 0 && (
              <i className={`fa-solid fa-chevron-right collapsible-icon ${extensionsExpanded ? 'expanded' : ''}`}></i>
            )}
          </button>
          {!sidebarCollapsed && (
            <div className={`sidebar-submenu ${extensionsExpanded ? 'expanded' : ''}`}>
              {activatedExtensions.map(ext => (
                <button
                  key={ext.id}
                  className={`sidebar-submenu-item ${activeExtensionSettings === ext.id ? 'active' : ''} ${
                    recentlyAddedExtension === ext.id ? 'newly-added' : ''
                  }`}
                  onClick={() => onExtensionClick(ext.id)}
                >
                  {ext.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={() => onSectionChange('custom-commands')}
          className={`sidebar-item flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-2 cursor-pointer w-full text-left ${
            activeSection === 'custom-commands' ? 'active' : ''
          }`}
          title={sidebarCollapsed ? 'custom commands' : ''}
        >
          <i className="fa-solid fa-terminal w-5 text-center"></i>
          {!sidebarCollapsed && <span>custom commands</span>}
        </button>

        <button
          onClick={() => onSectionChange('timed-messages')}
          className={`sidebar-item flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-2 cursor-pointer w-full text-left ${
            activeSection === 'timed-messages' ? 'active' : ''
          }`}
          title={sidebarCollapsed ? 'timed messages' : ''}
        >
          <i className="fa-solid fa-clock w-5 text-center"></i>
          {!sidebarCollapsed && <span>timed messages</span>}
        </button>

        <button
          onClick={() => onSectionChange('moderation')}
          className={`sidebar-item flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-2 cursor-pointer w-full text-left ${
            activeSection === 'moderation' ? 'active' : ''
          }`}
          title={sidebarCollapsed ? 'moderation' : ''}
        >
          <i className="fa-solid fa-shield-halved w-5 text-center"></i>
          {!sidebarCollapsed && <span>moderation</span>}
        </button>

        {user && (
          <button
            onClick={() => onSectionChange('settings')}
            className={`sidebar-item flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} p-2 cursor-pointer w-full text-left ${
              activeSection === 'settings' ? 'active' : ''
            }`}
            title={sidebarCollapsed ? 'settings' : ''}
          >
            <i className="fa-solid fa-cog w-5 text-center"></i>
            {!sidebarCollapsed && <span>settings</span>}
          </button>
        )}
      </nav>
      
      {!sidebarCollapsed && (
        <div className="my-4">
          <p className="text-center text-xs text-gray-400">
            made with ❤️ by{' '}
            <a
              href="https://x.com/whizkick"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 transition-colors"
            >
              whiz
            </a>
          </p>
        </div>
      )}

      <div className="border-t-2 border-white pt-4" id="auth-section">
        {!user ? (
          <div id="logged-out">
            <button
              className={`paint-button w-full ${sidebarCollapsed ? 'p-2' : ''}`}
              onClick={onLogin}
              title={sidebarCollapsed ? 'Sign In' : ''}
            >
              <i className={`fa-solid fa-right-to-bracket ${!sidebarCollapsed ? 'mr-2' : ''}`}></i>
              {!sidebarCollapsed && <span>Sign In</span>}
            </button>
          </div>
        ) : (
          <div id="logged-in" className={`${sidebarCollapsed ? '' : 'text-center'}`}>
            {!sidebarCollapsed && (
              <>
                <p className="mb-2 text-xs text-gray-400">Signed in as</p>
                <p className="mb-3 text-white font-medium truncate px-2" title={user.email}>
                  {user.email}
                </p>
              </>
            )}
            <button
              className={`paint-button logout-button w-full ${sidebarCollapsed ? 'p-2' : ''}`}
              onClick={onLogout}
              title={sidebarCollapsed ? `Sign Out (${user.email})` : ''}
            >
              <i className={`fa-solid fa-right-from-bracket ${!sidebarCollapsed ? 'mr-2' : ''}`}></i>
              {!sidebarCollapsed && <span>Sign Out</span>}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;